import {darken, lighten, alpha, type Components} from "@mui/material/styles";
import type {Theme} from "../..";

type MuiButtonOverrides = Components<Theme>["MuiButton"];

/**
 * @typedef {boolean} ButtonInverted
 * @typedef {("primary" | "error")} ButtonColor
 * @typedef {("small" | "medium" | "large" | "extraSmall")} ButtonSize
 */
declare module "@mui/material/Button" {
    interface ButtonPropsVariantOverrides {
        outlined: true;
        contained: true;
        text: true;
    }

    interface ButtonPropsSizeOverrides {
        extraSmall: true;
        small: true;
        medium: true;
        large: true;
    }

    interface ButtonOwnProps {
        /**
         * If `true`, the button will "invert" its color scheme.
         * For use on backgrounds that need higher contrast.
         *
         * @default false
         *
         * @param {ButtonInverted} [inverted] - This prop is only available in the Next theme.
         * It is not supported by the classic Toolkit theme
         */
        inverted?: boolean;
        /**
         * @param {ButtonColor} [color] - "primary" | "error" - The Next theme has only: "primary" and "error".
         * Don't use any other variants if you want the Next theme.
         */
        color?:
            | "inherit"
            | "primary"
            | "secondary"
            | "success"
            | "error"
            | "info"
            | "warning";
        /**
         * @param {ButtonSize} [size] - The Next theme supports an extra size: "extraSmall"
         * It is not supported by the classic Toolkit theme
         */
        size?: "small" | "medium" | "large" | "extraSmall";
    }
}

const overrides: MuiButtonOverrides = {
    defaultProps: {
        color: "primary",
        disableRipple: true,
        disableFocusRipple: true,
        disableElevation: true,
    },
    styleOverrides: {
        root: ({ownerState, theme}) => ({
            transition: theme.transitions.create(
                ["background-color", "box-shadow", "color"],
                {
                    duration: 0,
                },
            ),
            borderRadius: theme.borderRadius.full,
            ...(ownerState.size === "large" && {
                ...theme.typography.title4,
            }),
            ...(ownerState.size === "medium" && {
                ...theme.typography.title5,
            }),
            ...(ownerState.size === "small" && {
                ...theme.typography.title5,
            }),
            ...(ownerState.size === "extraSmall" && {
                ...theme.typography.title6,
            }),
            fontWeight: theme.typography.fontWeight.medium,

            ...(ownerState.variant === "contained" && {
                color: theme.color.text.inverted.default,
                boxShadow: theme.shadows[4],

                ...(ownerState.size === "large" && {
                    padding: "12px 32px",
                }),
                ...(ownerState.size === "medium" && {
                    padding: "10px 24px",
                }),
                ...(ownerState.size === "small" && {
                    padding: "6px 16px",
                }),
                ...(ownerState.size === "extraSmall" && {
                    padding: "4px 14px",
                }),

                ...(ownerState.color === "primary" && {
                    backgroundColor: ownerState.inverted
                        ? theme.color.button.primary.inverted.default
                        : theme.color.button.primary.default,
                    ":hover": {
                        backgroundColor: lighten(
                            ownerState.inverted
                                ? theme.color.button.primary.inverted.default
                                : theme.color.button.primary.default,
                            0.08,
                            // 0.04,
                        ),
                    },
                    ":active": {
                        backgroundColor: darken(
                            ownerState.inverted
                                ? theme.color.button.primary.inverted.default
                                : theme.color.button.primary.default,
                            0.12,
                            // 0.08,
                        ),
                    },
                }),

                ...(ownerState.color === "error" && {
                    backgroundColor: ownerState.inverted
                        ? theme.color.button.primary.inverted.critical
                        : theme.color.button.primary.critical,
                    ":hover": {
                        backgroundColor: lighten(
                            ownerState.inverted
                                ? theme.color.button.primary.inverted.critical
                                : theme.color.button.primary.critical,
                            0.08,
                            // 0.04,
                        ),
                    },
                    ":active": {
                        backgroundColor: darken(
                            ownerState.inverted
                                ? theme.color.button.primary.inverted.critical
                                : theme.color.button.primary.critical,
                            0.12,
                            // 0.08,
                        ),
                    },
                }),

                ...(ownerState.disabled && {
                    color: ownerState.inverted
                        ? theme.color.text.inverted.disabled
                        : theme.color.text.disabled,
                    backgroundColor: ownerState.inverted
                        ? theme.color.interaction.disabled
                        : theme.color.button.disabled,
                }),
            }),

            ...(ownerState.variant === "outlined" && {
                color: ownerState.inverted
                    ? theme.color.text.inverted.default
                    : theme.color.text.interactive,
                borderColor: "transparent",

                ...(ownerState.size === "large" && {
                    padding: "12px 32px",
                }),
                ...(ownerState.size === "medium" && {
                    padding: "10px 24px",
                }),
                ...(ownerState.size === "small" && {
                    padding: "6px 16px",
                }),
                ...(ownerState.size === "extraSmall" && {
                    padding: "4px 14px",
                }),

                ...(ownerState.color === "primary" && {
                    backgroundColor: ownerState.inverted
                        ? alpha(theme.color.text.inverted.default, 0.15)
                        : theme.color.button.secondary.default,
                    ":hover": {
                        backgroundColor: ownerState.inverted
                            ? alpha(theme.color.text.inverted.default, 0.23)
                            : darken(theme.color.button.secondary.default, 0.1),
                        borderColor: "transparent",
                    },
                    ":active": {
                        backgroundColor: ownerState.inverted
                            ? alpha(theme.color.text.inverted.default, 0.12)
                            : darken(theme.color.button.secondary.default, 0.15),
                    },
                }),

                ...(ownerState.color === "error" && {
                    backgroundColor: theme.color.button.secondary.critical,
                    color: ownerState.inverted
                        ? theme.color.button.primary.critical
                        : theme.color.text.critical,
                    ":hover": {
                        backgroundColor: ownerState.inverted
                            ? alpha(theme.color.button.primary.critical, 0.12)
                            : darken(theme.color.button.secondary.critical, 0.15),
                        borderColor: "transparent",
                    },
                    ":active": {
                        backgroundColor: ownerState.inverted
                            ? alpha(theme.color.button.primary.critical, 0.08)
                            : darken(theme.color.button.secondary.critical, 0.36),
                    },
                }),

                ...(ownerState.disabled && {
                    color: theme.color.text.disabled,
                    backgroundColor: theme.color.button.disabled,
                }),
            }),

            ...(ownerState.variant === "text" && {
                ...(ownerState.size === "large" && {
                    padding: "5px 8.5px",
                }),
                ...(ownerState.size === "medium" && {
                    padding: "4px 6.5px",
                }),
                ...(ownerState.size === "small" && {
                    padding: "2px 6.5px",
                }),
                ...(ownerState.size === "extraSmall" && {
                    padding: "2px 5px",
                }),

                ...(ownerState.color === "primary" && {
                    color: ownerState.inverted
                        ? theme.color.text.inverted.default
                        : theme.color.text.interactive,
                    ":hover": {
                        backgroundColor: "unset",
                        color: darken(
                            ownerState.inverted
                                ? theme.color.text.inverted.default
                                : theme.color.text.interactive,
                            0.08,
                        ),
                        textDecoration: "underline",
                        textUnderlineOffset: 3,
                    },
                    ":active": {
                        color: ownerState.inverted
                            ? darken(theme.color.text.inverted.default, 0.36)
                            : darken(theme.color.text.interactive, 0.36),
                        textDecoration: "underline",
                        textUnderlineOffset: 3,
                    },
                }),

                ...(ownerState.color === "error" && {
                    color: ownerState.inverted
                        ? theme.color.button.primary.critical
                        : theme.color.text.critical,
                    ":hover": {
                        backgroundColor: "unset",
                        color: darken(
                            ownerState.inverted
                                ? theme.color.button.primary.critical
                                : theme.color.text.critical,
                            0.04,
                        ),
                        textDecoration: "underline",
                        textUnderlineOffset: 3,
                    },
                    ":active": {
                        color: ownerState.inverted
                            ? darken(theme.color.button.primary.critical, 0.15)
                            : darken(theme.color.text.critical, 0.36),
                        textDecoration: "underline",
                        textUnderlineOffset: 3,
                    },
                }),

                ...(ownerState.disabled && {
                    color: ownerState.inverted
                        ? theme.color.text.inverted.disabled
                        : theme.color.text.disabled,
                }),
            }),
        }),
    },
};

export default overrides;
